import AddressModel from '$gbusiness/models/address';
import { toLocal } from '$gbusiness/helpers/date';
import { toNumber } from '$gbusiness/helpers/util';
import DiscountModel, { deriveRawToDiscount } from './discount';
import TermModel, { deriveRawToTerm } from './term';
import { deriveRawToUser } from '$gbusiness/models/user';
import BankModel, { deriveRawToBank } from './bank';

export default interface StoreModel {
  id: number;
  factoryId: number;
  active: boolean;
  hasInvoice?: boolean;
  disabled?: boolean;
  code?: string;
  name: string;
  formatted?: string;
  address?: AddressModel | undefined;
  shippingAddress?: AddressModel | undefined;
  placeId?: string;
  email: string;
  username: string;
  phone: string;
  discounts?: Array<DiscountModel>;
  userIds?: Array<number>;
  regionId?: number;
  regionIds?: Array<number>;
  regionName?: string;
  credits?: number;
  termId?: number;
  terms?: Array<TermModel>;
  discountId?: number;
  discount?: DiscountModel;
  openBalance: number;
  warehouseId?: number;
  balanceOwed: number;
  storeLevelId?: number;
  salesmen?: Array<string>;
  banks: Array<BankModel>;
  settings: {
    taxPercent: 0;
    note?: string;
    wholesale: string;
    emailConfirmation: boolean;
    textConfirmation: boolean;
    discounts?: Array<number>;
    defaultDiscount?: number;
    invoiceEmail: string;
  };
  createdAt?: string;
}

export interface ComplexStoreModel extends StoreModel {
  salesmen;
}

export interface SimpleStoreModel {
  id: number;
  name: string;
  formatted: string;
}

export const deriveRawToStore = (raw) => {
  return {
    id: raw.id,
    factoryId: raw.factoryId,
    code: raw.code || '',
    active: !!raw.active,
    disabled: !raw.active,
    hasInvoice: !!raw.hasInvoice,
    name: raw.name,
    formatted: raw.address?.formatted || '',
    settings: {
      ...raw.settings,
      wholesale: raw.settings?.wholesale === '1' ? '1' : '0',
      taxPercent: toNumber(raw.settings?.taxPercent),
    },
    ...(raw.address && { address: raw.address }),
    ...(raw.shippingAddress && { shippingAddress: raw.shippingAddress }),
    ...(raw.discounts && { discounts: raw.discounts.map(deriveRawToDiscount) }),
    ...(raw.terms && { terms: raw.terms.map(deriveRawToTerm) }),
    ...(raw.userIds && { userIds: raw.userIds }),
    ...(raw.regionId && { regionId: raw.regionId }),
    ...(raw.regionIds && { regionIds: raw.regionIds }),
    ...(raw.regionName && { regionName: raw.regionName }),
    ...(raw.termId && { termId: raw.termId, term: raw.term }),
    ...(raw.discountId && { discountId: raw.discountId, discount: raw.discount }),
    ...(raw.warehouse && { warehouseId: raw.warehouse.id }),
    ...(raw.storeLevel && { storeLevelId: raw.storeLevel.id }),
    banks: (raw.banks || []).map(deriveRawToBank),
    openBalance: raw.openBalance || 0,
    balanceOwed: raw.balanceOwed || 0,
    salesmen: (raw.salesmen || []).map(deriveRawToUser) || [],
    placeId: raw.placeId,
    credits: raw.credits || 0,
    email: raw.email,
    username: raw.username,
    phone: raw.phone,
    createdAt: toLocal(raw.createdAt),
  };
};

export const initialStore: StoreModel = {
  id: 0,
  factoryId: 0,
  name: '',
  active: true,
  formatted: '',
  placeId: '',
  email: '',
  username: '',
  phone: '',
  banks: [],
  credits: 0,
  openBalance: 0,
  balanceOwed: 0,
  settings: {
    taxPercent: 0,
    wholesale: '0',
    emailConfirmation: true,
    textConfirmation: false,
    invoiceEmail: '',
  },
};
